/* Shared WebAudio graph for the sound effects.
 *
 *   sfx sounds ──► sfxBus ──┬─────────────► compressor ─► destination
 *                           └─► reverb ───►     ▲
 *
 * The convolver uses a generated impulse response (no audio files), the
 * compressor glues everything together and protects against clipping. */

interface AudioSettingsGetters {
  muted: () => boolean;
  sfxVolume: () => number; // 0..100
  musicVolume: () => number; // 0..100
}

let getters: AudioSettingsGetters = {
  muted: () => false,
  sfxVolume: () => 80,
  musicVolume: () => 50,
};

let ctx: AudioContext | null = null;
let sfxBusNode: GainNode | null = null;
let musicBusNode: GainNode | null = null;
let reverbNode: ConvolverNode | null = null;
let noiseBuf: AudioBuffer | null = null;

export function bindAudioSettings(g: AudioSettingsGetters): void {
  getters = g;
  applyVolumes();
}

function makeImpulseResponse(c: AudioContext, seconds: number, decay: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.exp((-decay * i) / c.sampleRate);
    }
  }
  return buf;
}

/** Lazily creates the graph; safe to call often (also resumes a suspended ctx). */
export function ac(): AudioContext | null {
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();

    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 24;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.24;
    comp.connect(ctx.destination);

    sfxBusNode = ctx.createGain();
    sfxBusNode.connect(comp);

    musicBusNode = ctx.createGain();
    musicBusNode.connect(comp);

    reverbNode = ctx.createConvolver();
    reverbNode.buffer = makeImpulseResponse(ctx, 1.1, 5.5);
    const reverbReturn = ctx.createGain();
    reverbReturn.gain.value = 0.35;
    reverbNode.connect(reverbReturn);
    reverbReturn.connect(comp);

    const len = ctx.sampleRate;
    noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

    applyVolumes();
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function applyVolumes(): void {
  if (!ctx || !sfxBusNode) return;
  const t = ctx.currentTime;
  const muted = getters.muted();
  sfxBusNode.gain.setTargetAtTime(muted ? 0 : (getters.sfxVolume() / 100) * 0.6, t, 0.05);
  if (musicBusNode) {
    musicBusNode.gain.setTargetAtTime(muted ? 0 : (getters.musicVolume() / 100) * 0.32, t, 0.05);
  }
}

export const sfxAudible = (): boolean => !getters.muted() && getters.sfxVolume() > 0 && !!ac();

export function sfxBus(): GainNode | null {
  ac();
  return sfxBusNode;
}

export function musicBus(): GainNode | null {
  ac();
  return musicBusNode;
}

export function noiseBuffer(): AudioBuffer | null {
  ac();
  return noiseBuf;
}

/** Connects a node to a bus with an additional reverb send. */
export function routeWithReverb(node: AudioNode, bus: GainNode, wet: number): void {
  node.connect(bus);
  if (reverbNode && wet > 0) {
    const send = bus.context.createGain();
    send.gain.value = wet;
    node.connect(send);
    send.connect(reverbNode);
  }
}
